"use client";

import { useQueries, useQuery } from "@tanstack/react-query";

import type {
  BomResponse,
  CustomerResponse,
  ListCustomersQuery,
  ListProductsQuery,
  ListStocksQuery,
  ProductDetailResponse,
  ProductResponse,
  PurchaseOrderRequirementsResponse,
  PurchaseOrderResponse,
  StockItemResponse,
} from "@stocks/shared";

import { Get, GetList } from "@/shared/libs/axios/request";
import { usePaginatedList } from "@/shared/libs/query";

// 목록 키는 usePaginatedList 의 [key, "list", query] 와 접두사를 공유하므로
// invalidateQueries({ queryKey: [orderKeys.customers] }) 한 번으로 목록·상세가 함께 갱신된다.
export const orderKeys = {
  customers: "customers",
  products: "products",
  orders: "purchase-orders",
  product: (id: string) => ["products", "detail", id] as const,
  bom: (productId: string) => ["products", "bom", productId] as const,
  order: (id: string) => ["purchase-orders", "detail", id] as const,
  requirements: (id: string) => ["purchase-orders", "requirements", id] as const,
  availability: (itemId: string) => ["stocks", "availability", itemId] as const,
};

const OPTION_QUERY = { pageSize: 100, sort: "name", order: "asc" } as const;

/** 발주 입력 시 선택용 고객 목록 */
export const useCustomerOptions = () =>
  usePaginatedList<CustomerResponse, ListCustomersQuery>(
    orderKeys.customers,
    "/v1/customers",
    { ...OPTION_QUERY, isActive: true },
  );

/** 발주 입력 시 선택용 생산 제품 목록 */
export const useProductOptions = () =>
  usePaginatedList<ProductResponse, ListProductsQuery>(
    orderKeys.products,
    "/v1/products",
    { ...OPTION_QUERY, isActive: true },
  );

/** 제품 상세 (색상/사이즈 옵션 포함) */
export const useProduct = (id: string) =>
  useQuery({
    queryKey: orderKeys.product(id),
    enabled: id !== "",
    queryFn: async () =>
      (await Get<ProductDetailResponse, never>(`/v1/products/${id}`)).data.data,
  });

const fetchBom = (productId: string) =>
  Get<BomResponse, never>(`/v1/products/${productId}/bom`)
    .then((res) => res.data.data ?? null)
    // BOM 이 아직 없으면 서버가 404 — 빈 BOM 으로 취급
    .catch((err: ApiError) => {
      if (err.response?.status === 404) return null;
      throw err;
    });

/** 제품의 현재(최신) BOM, 없으면 null */
export const useBom = (productId: string) =>
  useQuery({
    queryKey: orderKeys.bom(productId),
    enabled: productId !== "",
    queryFn: () => fetchBom(productId),
  });

/** 여러 제품의 BOM 을 한 번에 조회 (발주 행의 필요 자재 계산용) */
export const useBoms = (productIds: string[]) => {
  const results = useQueries({
    queries: productIds.map((id) => ({
      queryKey: orderKeys.bom(id),
      queryFn: () => fetchBom(id),
    })),
  });
  const boms = new Map<string, BomResponse | null | undefined>();
  productIds.forEach((id, i) => boms.set(id, results[i]?.data));
  return { boms, isLoading: results.some((r) => r.isLoading) };
};

/** 품목별 가용 수량 (유효기한이 지난 로트 제외, 음수 로트는 0) */
export const useAvailability = (itemIds: string[]) => {
  const results = useQueries({
    queries: itemIds.map((id) => ({
      queryKey: orderKeys.availability(id),
      queryFn: async () => {
        const res = await GetList<StockItemResponse, ListStocksQuery>(
          "/v1/stocks",
          { itemId: id, pageSize: 1 },
        );
        const lots = res.data.data?.items[0]?.lots ?? [];
        const now = Date.now();
        return lots.reduce((sum, lot) => {
          if (lot.expiresAt && new Date(lot.expiresAt).getTime() <= now) return sum;
          return sum + Math.max(0, Number(lot.available));
        }, 0);
      },
    })),
  });
  const availability = new Map<string, number>();
  itemIds.forEach((id, i) => availability.set(id, results[i]?.data ?? 0));
  return { availability, isLoading: results.some((r) => r.isLoading) };
};

export const usePurchaseOrder = (id: string) =>
  useQuery({
    queryKey: orderKeys.order(id),
    enabled: id !== "",
    queryFn: async () =>
      (await Get<PurchaseOrderResponse, never>(`/v1/purchase-orders/${id}`)).data
        .data,
  });

/** 저장된 발주의 필요 자재·부족 수량 (서버 계산 — BOM 버전 고정, 가용 수량 대조) */
export const useRequirements = (id: string) =>
  useQuery({
    queryKey: orderKeys.requirements(id),
    enabled: id !== "",
    queryFn: async () =>
      (
        await Get<PurchaseOrderRequirementsResponse, never>(
          `/v1/purchase-orders/${id}/requirements`,
        )
      ).data.data,
  });
